import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Navigate } from 'react-router';
import { RefreshIcon } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { AuthLayout } from '#components/patterns/layouts/auth/AuthLayout.tsx';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import type { InvalidMagicLinkType } from '../../types.ts';

type Props = {
  email?: string;
  onResent: () => void;
  type?: InvalidMagicLinkType;
};

type FormData = {
  email: string;
};

export function MagicLinkInvalid({ email, onResent, type = 'expired' }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>();
  const { sendMagicLink } = useAuth();
  const sendMagicLinkMutation = useMutation({ mutationFn: sendMagicLink });
  const { user } = useAuth();

  async function resendMagicLink(emailAddress: string) {
    await sendMagicLinkMutation.mutateAsync(emailAddress);
    onResent();
  }

  // If the user is already logged in, we don't allow getting a new magic-link, just take them to the app
  if (user && user.type === 'user' && user.email === email) {
    return <Navigate to="/" replace={true} />;
  }

  return (
    <AuthLayout headerText={<span className="truncate">{email}</span>}>
      <div className="simple-page-content">
        <h1 className="h2 mb-8">Magic link {type}</h1>
        <p className="p-bold mb-32">We can send you a new one, though!</p>
        <form
          method="post"
          onSubmit={handleSubmit(async (data) => {
            await resendMagicLink(data.email);
          })}
        >
          <Field id="email" label="What is your email?" className={email ? undefined : 'mb-32'} hidden={Boolean(email)}>
            <Input
              className="width-100"
              type="email"
              defaultValue={email}
              hasError={Boolean(errors.email)}
              enablePWManagers={true}
              {...register('email', { required: 'What’s your email address?' })}
            />
            {errors.email && <ErrorMessage className="mt-8">{errors.email.message}</ErrorMessage>}
          </Field>
          <Button className="width-100 mb-8" disabled={isSubmitting} type="submit">
            {email ?
              <RefreshIcon className="mr-4" />
            : null}{' '}
            Send new magic link
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
